import { useState, useEffect } from "react";
import styles from "./CommunityMembersPopup.module.css";
import { FiSearch, FiDownload } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { toast } from "react-toastify";

const ROLE_LABELS = { admin: "Admin", trader: "Trader", user: "User" };

// Wrap a value in quotes when it contains a comma, quote or newline.
const escapeCsv = (val) => {
  const s = String(val ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const CommunityMembersPopup = ({
  scope = "community",
  id,
  name,
  type,
  onClose,
}) => {
  const isTraders = type === "traders";
  const isChannel = scope === "channel";
  const title = isTraders ? "Traders" : "Members";

  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({
    admin: 0,
    trader: 0,
    user: 0,
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchList = async () => {
      try {
        setIsLoading(true);
        const endpoint = isChannel
          ? isTraders
            ? ENDPOINTS.GET_CHANNEL_TRADERS(id)
            : ENDPOINTS.GET_CHANNEL_MEMBERS(id)
          : isTraders
            ? ENDPOINTS.GET_COMMUNITY_TRADERS(id)
            : ENDPOINTS.GET_COMMUNITY_MEMBERS(id);
        const { data } = await api.get(endpoint);
        if (data.success) {
          setUsers(data.users || []);
          setCounts(
            data.counts || { admin: 0, trader: 0, user: 0, total: 0 }
          );
        }
      } catch (err) {
        console.error("Failed to fetch community users", err);
        toast.error(`Failed to fetch ${isTraders ? "traders" : "members"}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchList();
  }, [id, isChannel, isTraders]);

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      (u.display_name || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const countParts = [];
  if (counts.admin) countParts.push(`${counts.admin} admin`);
  if (counts.trader) countParts.push(`${counts.trader} trader`);
  if (counts.user) countParts.push(`${counts.user} user`);

  // Export the currently filtered rows as a CSV file.
  const handleExport = () => {
    const header = ["Display name", "Username", "Email", "Role"];
    const rows = filteredUsers.map((u) => [
      u.display_name || "",
      u.username || "",
      u.email || "",
      ROLE_LABELS[u.role] || u.role || "",
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\r\n");

    // Prepend a BOM so Excel reads UTF-8 correctly.
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const safeName = (name || scope).replace(/[^\w-]+/g, "_");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title}-${safeName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          &times;
        </button>

        <div className={styles.header}>
          <h2 className={styles.title}>
            {title}
            {name ? ` — ${name}` : ""}
          </h2>
          {!isLoading && (
            <p className={styles.subtitle}>
              {countParts.length
                ? countParts.join("  ·  ")
                : `No ${title.toLowerCase()}`}
            </p>
          )}
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Type in to search .."
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FiSearch className={styles.searchIcon} />
          </div>
          <button
            className={styles.exportBtn}
            onClick={handleExport}
            disabled={isLoading || filteredUsers.length === 0}
          >
            <FiDownload /> Export CSV
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>
              Loading {title.toLowerCase()}...
            </p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <p className={styles.empty}>No {title.toLowerCase()} found</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Display name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.display_name || "-"}</td>
                    <td>{u.username || "-"}</td>
                    <td>{u.email || "-"}</td>
                    <td>
                      <span
                        className={`${styles.badge} ${
                          styles[u.role] || ""
                        }`}
                      >
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityMembersPopup;
